/**
 * Device preferences — per-user audio device selection.
 *
 * Stored in Supabase `telephony_devices` (keyed on user_id) so the choice
 * syncs across laptops. In-memory cache avoids a round-trip on every dial.
 */

import { supabase } from '../supabase';

export interface DevicePreferences {
  microphoneId: string | null;
  microphoneLabel: string | null;
  speakerId: string | null;
  speakerLabel: string | null;
  /** Apart beltoon-apparaat; null = volg de gesprek-speaker. */
  ringtoneSpeakerId: string | null;
  ringtoneSpeakerLabel: string | null;
  ringtoneVolume: number;
  notificationsGranted: boolean;
}

let cached: DevicePreferences | null = null;
let loadPromise: Promise<DevicePreferences | null> | null = null;

const DEFAULTS: DevicePreferences = {
  microphoneId: null,
  microphoneLabel: null,
  speakerId: null,
  speakerLabel: null,
  ringtoneSpeakerId: null,
  ringtoneSpeakerLabel: null,
  ringtoneVolume: 0.3,
  notificationsGranted: false,
};

export function getCached(): DevicePreferences {
  return cached ?? DEFAULTS;
}

/**
 * Merge a preference patch into the in-memory cache WITHOUT a DB write.
 * Used on the owner tab when a follower tab changed devices (proxied over the
 * phone-bus): the follower already persisted to Supabase; the owner only needs
 * its local cache updated so ringtone/sink/mic pick up the new choice.
 */
export function applyCachePatch(patch: Partial<DevicePreferences>): void {
  cached = { ...(cached ?? DEFAULTS), ...patch };
}

export async function loadPreferences(): Promise<DevicePreferences | null> {
  if (cached) return cached;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('telephony_devices')
        .select(
          'microphone_id, microphone_label, speaker_id, speaker_label, ringtone_speaker_id, ringtone_speaker_label, ringtone_volume, notifications_granted',
        )
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.warn('[telephony/devices] load failed', error);
        return null;
      }
      if (!data) {
        cached = { ...DEFAULTS };
        return cached;
      }

      cached = {
        microphoneId: data.microphone_id ?? null,
        microphoneLabel: data.microphone_label ?? null,
        speakerId: data.speaker_id ?? null,
        speakerLabel: data.speaker_label ?? null,
        ringtoneSpeakerId: data.ringtone_speaker_id ?? null,
        ringtoneSpeakerLabel: data.ringtone_speaker_label ?? null,
        ringtoneVolume: Number(data.ringtone_volume ?? 0.3),
        notificationsGranted: Boolean(data.notifications_granted),
      };
      return cached;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

export async function savePreferences(patch: Partial<DevicePreferences>): Promise<void> {
  const current = cached ?? (await loadPreferences()) ?? { ...DEFAULTS };
  const next: DevicePreferences = { ...current, ...patch };
  cached = next;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.from('telephony_devices').upsert(
      {
        user_id: user.id,
        microphone_id: next.microphoneId,
        microphone_label: next.microphoneLabel,
        speaker_id: next.speakerId,
        speaker_label: next.speakerLabel,
        ringtone_speaker_id: next.ringtoneSpeakerId,
        ringtone_speaker_label: next.ringtoneSpeakerLabel,
        ringtone_volume: next.ringtoneVolume,
        notifications_granted: next.notificationsGranted,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );

    if (error) {
      console.warn('[telephony/devices] save failed', error);
    }
  } catch (err) {
    console.warn('[telephony/devices] save threw', err);
  }
}

export async function enumerate(): Promise<{
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return { microphones: [], speakers: [] };
  }
  const all = await navigator.mediaDevices.enumerateDevices();
  return {
    microphones: all.filter((d) => d.kind === 'audioinput'),
    speakers: all.filter((d) => d.kind === 'audiooutput'),
  };
}

/**
 * Ask the browser for mic permission so subsequent enumerateDevices() returns
 * labels instead of empty strings. Stops the stream immediately after.
 */
export async function primeDeviceLabels(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}
